import React, { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { OfflineEditableDataGrid } from '../components/OfflineEditableDataGrid'
import { ItemSearchResult } from '../components/OfflineItemSearchModal'
import { draftInvoiceService, DraftInvoiceItem } from '../services/draftInvoiceService'
import { getFinalItemPriceDetails } from '@/app/lib/salePriceRuleUtils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// Form schema for offline invoice
const offlineInvoiceSchema = z.object({
  customer_name: z.string().optional(),
  customer_phone: z.string().optional(),
  transaction_date: z.date(),
  notes: z.string().optional()
})

type OfflineInvoiceFormData = z.infer<typeof offlineInvoiceSchema>

interface OfflineInvoiceFormProps {
  initialData?: {
    id?: number
    customer_name?: string
    customer_phone?: string
    transaction_date?: string
    notes?: string
    items?: DraftInvoiceItem[]
  }
  onSuccess?: () => void
}

export const OfflineInvoiceForm: React.FC<OfflineInvoiceFormProps> = ({
  initialData,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [items, setItems] = useState<DraftInvoiceItem[]>(
    initialData?.items || [
      {
        item_id: 0,
        item_name: '',
        quantity: 1,
        rate: 0,
        discount_percent: 0,
        discount_amount: 0,
        tax_amount: 0,
        amount: 0
      }
    ]
  )

  const form = useForm<OfflineInvoiceFormData>({
    resolver: zodResolver(offlineInvoiceSchema),
    defaultValues: {
      customer_name: initialData?.customer_name || '',
      customer_phone: initialData?.customer_phone || '',
      transaction_date: initialData?.transaction_date
        ? new Date(initialData.transaction_date)
        : new Date(),
      notes: initialData?.notes || ''
    }
  })

  // Calculate totals
  const totals = useMemo(() => {
    return draftInvoiceService.calculateTotals(items)
  }, [items])

  // Grid columns configuration
  const columns = [
    {
      field: 'item_name',
      header: 'Product / Service',
      width: '300px',
      type: 'product' as const
    },
    {
      field: 'quantity',
      header: 'Qty',
      width: '80px',
      type: 'number' as const
    },
    {
      field: 'rate',
      header: 'Rate',
      width: '120px',
      type: 'price' as const
    },
    {
      field: 'discount_percent',
      header: 'Disc %',
      width: '80px',
      type: 'number' as const
    },
    {
      field: 'discount_amount',
      header: 'Disc Amt',
      width: '100px',
      type: 'price' as const,
      editable: false
    },
    {
      field: 'tax_amount',
      header: 'Tax',
      width: '100px',
      type: 'price' as const,
      editable: false
    },
    {
      field: 'amount',
      header: 'Amount',
      width: '120px',
      type: 'price' as const,
      editable: false
    },
    {
      field: 'actions',
      header: '',
      width: '50px',
      type: 'actions' as const
    }
  ]

  // Search products using SQLite
  const searchProducts = async (query: string): Promise<ItemSearchResult[]> => {
    try {
      const results = await window.api.db.searchItems(query)
      return results
    } catch (error) {
      console.error('Error searching products:', error)
      return []
    }
  }

  // Handle product selection
  const handleProductSelect = async (
    product: ItemSearchResult,
    rowIndex: number
  ): Promise<void> => {
    try {
      // Get item price details - call with correct parameters
      const priceDetails = await getFinalItemPriceDetails(
        product.id, // itemId
        undefined, // manualPurchasePrice - let it fetch from DB
        null, // siteId - null for offline invoices
        null, // partyId - null for offline invoices
        items[rowIndex]?.quantity || 1, // quantity
        product.tax_rate_id // itemTaxRateId
      )

      // Update the row with price details
      const newItems = [...items]
      if (newItems[rowIndex]) {
        newItems[rowIndex] = {
          ...newItems[rowIndex],
          item_id: product.id,
          item_name: product.name,
          item_code: product.code,
          description: product.description || product.name,
          rate: Number(priceDetails.rate),
          discount_percent: Number(priceDetails.discount_percent),
          discount_amount: Number(priceDetails.discount_amount),
          tax_rate_id: priceDetails.tax_rate_id,
          tax_amount: Number(priceDetails.tax_amount),
          amount: Number(priceDetails.final_amount)
        }
        setItems(newItems)
      }
    } catch (error) {
      console.error('Error getting price details:', error)
      toast.error('Failed to get item price details')
    }
  }

  // Handle quantity change to recalculate prices
  const handleQuantityChangeForRow = async (
    rowIndex: number,
    newQuantity: number
  ): Promise<void> => {
    const item = items[rowIndex]
    if (!item || !item.item_id) return

    try {
      // Refetch price details with new quantity
      const priceDetails = await getFinalItemPriceDetails(
        item.item_id, // itemId
        undefined, // manualPurchasePrice
        null, // siteId
        null, // partyId
        newQuantity, // new quantity
        item.tax_rate_id // existing tax_rate_id
      )

      // Update the row with new calculated values
      const newItems = [...items]
      if (newItems[rowIndex]) {
        newItems[rowIndex] = {
          ...newItems[rowIndex],
          quantity: newQuantity,
          rate: Number(priceDetails.rate),
          discount_percent: Number(priceDetails.discount_percent),
          discount_amount: Number(priceDetails.discount_amount),
          tax_amount: Number(priceDetails.tax_amount),
          amount: Number(priceDetails.final_amount)
        }
        setItems(newItems)
      }
    } catch (error) {
      console.error('Error recalculating price on quantity change:', error)
      toast.error('Failed to recalculate prices')
    }
  }

  // Handle form submission
  const onSubmit = async (data: OfflineInvoiceFormData) => {
    // Filter out empty items
    const validItems = items.filter((item) => item.item_id > 0 && item.quantity > 0)

    if (validItems.length === 0) {
      toast.error('Please add at least one item')
      return
    }

    setIsLoading(true)
    try {
      const invoiceData = {
        ...data,
        transaction_date: format(data.transaction_date, 'yyyy-MM-dd'),
        total_amount: totals.total,
        tax_amount: totals.totalTax,
        discount_amount: totals.totalDiscount,
        items: validItems
      }

      const result = initialData?.id
        ? await draftInvoiceService.updateDraftInvoice(initialData.id, invoiceData)
        : await draftInvoiceService.createDraftInvoice(invoiceData)

      if (result.success) {
        toast.success(initialData?.id ? 'Draft invoice updated' : 'Draft invoice created')
        if (onSuccess) {
          onSuccess()
        }
      } else {
        toast.error(result.error || 'Failed to save draft invoice')
      }
    } catch (error) {
      console.error('Error saving draft invoice:', error)
      toast.error('Failed to save draft invoice')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{initialData?.id ? 'Edit Draft Invoice' : 'Create Draft Invoice'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Customer Info */}
            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="customer_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer Name (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Walk-in Customer" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="customer_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="1234567890" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="transaction_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'dd-MM-yyyy')
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Items Grid */}
            <div>
              <h3 className="text-sm font-medium mb-2">Items</h3>
              <OfflineEditableDataGrid
                columns={columns}
                data={items}
                setData={setItems}
                productSearchFn={searchProducts}
                onProductSelect={handleProductSelect}
                onQuantityChangeForRow={handleQuantityChangeForRow}
                disabled={isLoading}
              />
            </div>

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add any notes..." className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Totals */}
            <div className="border-t pt-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₹ {totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.totalDiscount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount:</span>
                    <span>- ₹ {totals.totalDiscount.toFixed(2)}</span>
                  </div>
                )}
                {totals.totalTax > 0 && (
                  <div className="flex justify-between">
                    <span>Tax:</span>
                    <span>+ ₹ {totals.totalTax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>₹ {totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : initialData?.id ? 'Update' : 'Save Draft'}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            onClick={() => window.print()}
          >
            Print
          </Button>
        </div>
      </form>
    </Form>
  )
}
